/**
 * Pure payload-building logic for RecordTestModal's submit path.
 *
 * Extracted from RecordTestModal.jsx (Brief 44) so Brief 37's RCD changes land
 * in a small, unit-tested diff instead of inside a thousand-line component.
 *
 * No `await`, no `api`, no `toast`, no `queryClient` here — every validation
 * failure that used to call `toast.error(...)` and return now comes back as an
 * `error` string on the return value, and the caller decides how to surface it.
 * The async orchestration (condition-6 handling, the condition-changed update,
 * the record mutation itself) stays in the component.
 */
import { formatForInput, todayForInput } from './dateUtils';
import { getDischargeStartTimeIso } from './sessionTimerUtils';
import { validateRcdResult, buildPushButtonTestPayload, PUSH_BUTTON_FAILURE_DEFECT, DID_NOT_TRIP_DEFECT } from './testValidators';
import {
  getTestOptionsForAssetType,
  WORKSHOP_CHECKLIST,
  isExitEmergencyAsset,
  isRcdAsset,
  isWorkshopAsset,
} from '../constants/AssetTestConfig';

/** Overall workshop result from per-item checklist answers, or null while incomplete. */
export function deriveWorkshopResult(checklistValues) {
  const allAnswered = WORKSHOP_CHECKLIST.every(({ key }) => checklistValues[key] !== undefined);
  if (!allAnswered) return null;
  return WORKSHOP_CHECKLIST.some(({ key }) => checklistValues[key] === 'fail') ? 'fail' : 'pass';
}

/**
 * Build the payload(s) for RecordTestModal's non-removed submit paths (RCD,
 * exit/emergency, workshop, generic). The condition-6 (removed/disconnected)
 * path is handled entirely in the component — it never reaches here.
 *
 * @param {string|null} [args.exitEmergencyOriginalText] — set by the caller
 *   when an existing exit/emergency record's stored `defects_found` could
 *   not be matched to exactly one dropdown option (ambiguous multi-reason
 *   text from before single-select, or text the dropdown can't represent)
 *   and the technician has not since picked a reason or marked it Pass.
 *   When set, the save preserves the existing result and leaves
 *   `defectsFound` unset entirely, so the original text survives untouched.
 *   See Brief 36 Part 3 — this is the data-loss guard.
 * @param {string|null} [args.rcdOriginalText] — the RCD equivalent of
 *   `exitEmergencyOriginalText`: set by the caller when an existing RCD
 *   record's stored `defects_found`, after stripping the reasons the
 *   measurements themselves derive, still can't be matched to exactly one
 *   dropdown option. Same guard, same reason — see Brief 37 Part 4.
 * @returns {{ payload: object|null, pushButtonPayload: object|null, error: string|null }}
 *   `error` is set (and `payload`/`pushButtonPayload` are null) when the
 *   caller should toast the message and abort the save.
 */
export function buildRecordTestPayload({
  asset,
  data,
  existingTestId,
  existingTest,
  sessionId,
  checklistValues,
  photoUrl,
  groupTimers,
  storeSiteId,
  exitEmergencyOriginalText = null,
  rcdOriginalText = null,
}) {
  const userText = (data.commentsRecommendations || '').trim();
  const testDateVal =
    existingTestId && existingTest?.test_date
      ? formatForInput(existingTest.test_date)
      : todayForInput();

  const payload = {
    assetId: asset.id,
    testDate: testDateVal,
    notes: userText || undefined,
    // Ensure photoUrl is sent if it exists
    photoUrl: photoUrl != null && photoUrl !== '' ? photoUrl : (data.photoUrl || undefined),
  };

  // Secondary row written alongside the primary result (RCD push-button only).
  let pushButtonPayload = null;

  if (sessionId != null && String(sessionId).trim() !== '') {
    payload.sessionId = String(sessionId).trim();
  }

  // --- RCD SUBMISSION LOGIC ---
  if (isRcdAsset(asset.asset_type)) {
    // Never default to Pass. The push-button check is a required result, so
    // refuse to save until the tech has explicitly answered it — matches the
    // inline card path in useTestSession.handleInlineRcdSave. The form's
    // `required` rule normally catches this first; this is the backstop.
    //
    // There is no visual-inspection gate here (Brief 37 — see that brief's
    // Part 0a: the card path never had one, so removing it here ends a
    // divergence rather than creating one). A visual-condition concern is
    // now a selectable reason ('Poor Condition' / 'Poor Fitment'), joined
    // with any measurement-derived reason exactly like every other asset
    // type's dropdown.
    if (data.rcdPushButton !== 'pass' && data.rcdPushButton !== 'fail') {
      return { payload: null, pushButtonPayload: null, error: 'Push button result required before saving' };
    }

    const pushPass = data.rcdPushButton === 'pass';
    const selectedReason = (data.rcdFailureReason || '').trim();
    const didNotTrip = selectedReason === DID_NOT_TRIP_DEFECT;
    const tripTimeStr = (data.rcdTripTimeMs ?? '').toString().trim();
    // Untouched text that couldn't be matched to exactly one dropdown option
    // after stripping the measurement-derived reasons (Brief 37 Part 4) —
    // never guess. The record is preserved as-is rather than recomputed, so
    // a blank trip time here means "not re-measured", not "invalid input" —
    // it must not block the save the way it would for a fresh result below.
    const preservingOriginal = !selectedReason && !!rcdOriginalText;
    let rcdValidation = null;

    // Persist the push-button check as its own test row so the report can report
    // that test on its own and the push_button compliance clock advances.
    pushButtonPayload = buildPushButtonTestPayload({
      assetId: asset.id,
      testDate: testDateVal,
      pushButtonStatus: data.rcdPushButton,
      sessionId,
    });

    // Validate Trip Time ONLY if the push button passed and the RCD actually
    // tripped (a "did not trip" record carries no measurement). Trip time is
    // a measurement, not a reason — its presence/absence is independent of
    // any selected reason.
    if (pushPass && !didNotTrip && !preservingOriginal) {
      rcdValidation = validateRcdResult(tripTimeStr, 'pass');
      if (!rcdValidation.valid) {
        return {
          payload: null,
          pushButtonPayload: null,
          error: rcdValidation.error || 'Please enter a valid Trip Time (ms)',
        };
      }
    }

    // Trip time is null whenever the push button failed OR the RCD did not trip.
    // While preserving an untouched ambiguous record, accept whatever valid
    // number is already in the field without the strict "must enter one" gate.
    const tripTimeMs = !pushPass || didNotTrip
      ? null
      : rcdValidation?.tripTimeMs
        ?? (preservingOriginal && tripTimeStr !== '' && !Number.isNaN(parseInt(tripTimeStr, 10))
          ? parseInt(tripTimeStr, 10)
          : null);

    payload.testType = 'Operating Time';

    if (preservingOriginal) {
      // Preserve the existing result and skip BOTH `defectsFound` and
      // `metrics` entirely (not even `null`) so the presence-driven update
      // leaves both columns alone. A technician who re-enters a trip time
      // here is not asserting a new measurement — they haven't picked a
      // reason, so nothing about the record has actually changed from the
      // tech's point of view. Same guard as exit/emergency's `defectsFound`.
      payload.result = existingTest?.result ?? 'fail';
    } else {
      // Pass requires: push button passed, a trip time was measured, that
      // time is within the limit, AND no failure reason is selected.
      const measuredPass = pushPass && !didNotTrip && rcdValidation?.result === 'pass';
      const result = measuredPass && !selectedReason ? 'pass' : 'fail';

      payload.result = result;
      payload.metrics = tripTimeMs != null ? { trip_time_ms: tripTimeMs } : null;

      if (result === 'fail') {
        // Derived reasons first, then the selected reason — mirrors the
        // order every other asset type joins in (measurement/mechanical
        // signal, then the technician's own pick).
        const parts = [];
        if (!pushPass) parts.push(PUSH_BUTTON_FAILURE_DEFECT);
        if (pushPass && !didNotTrip && rcdValidation?.autoDefect) parts.push(rcdValidation.autoDefect);
        if (selectedReason) parts.push(selectedReason);
        payload.defectsFound = parts.length ? parts.join(', ') : undefined;
      } else {
        // Explicit clear when moving fail -> pass on update.
        payload.defectsFound = null;
      }
    }

  // --- EXIT/EMERGENCY SUBMISSION LOGIC ---
  } else if (isExitEmergencyAsset(asset.asset_type)) {
    const reason = (data.failureReason || '').trim();

    payload.testType = '90-minute Discharge';

    // C1: stamp the real discharge start from the group timer (server-hydrated,
    // so both techs record the same value regardless of who saves).
    const startedAtIso = getDischargeStartTimeIso(asset, groupTimers, storeSiteId);
    if (startedAtIso != null) {
      payload.testStartedAt = startedAtIso;
    }

    if (!reason && exitEmergencyOriginalText) {
      // Untouched ambiguous/unrepresentable original text — never guess.
      // Preserve the existing result and skip `defectsFound` entirely (not
      // even `null`) so the presence-driven update leaves the column alone.
      payload.result = existingTest?.result ?? 'fail';
    } else {
      payload.result = reason ? 'fail' : 'pass';
      payload.defectsFound = reason || null;
    }

  // --- WORKSHOP EQUIPMENT SUBMISSION LOGIC ---
  } else if (isWorkshopAsset(asset.asset_type)) {
    const wr = deriveWorkshopResult(checklistValues);
    if (wr === null) {
      return { payload: null, pushButtonPayload: null, error: 'Complete all checklist items before saving' };
    }
    payload.testType = 'Inspection';
    payload.result = wr;
    payload.metrics = { ...checklistValues };

  // --- GENERIC ASSET SUBMISSION LOGIC ---
  } else {
    payload.testType = data.testType;
    payload.result = data.result;
    payload.defectsFound =
      data.result === 'fail'
        ? data.failureReason?.trim() || (userText || undefined)
        : null;

    const testOptions = getTestOptionsForAssetType(asset.asset_type);
    const selectedOption = testOptions.find((o) => o.value === data.testType);
    const showMetrics = selectedOption?.requiresMetrics === true;
    if (showMetrics && data.tripTimeMs !== '' && data.tripTimeMs != null) {
      const ms = parseInt(data.tripTimeMs, 10);
      if (!Number.isNaN(ms)) {
        payload.metrics = { trip_time_ms: ms };
      }
    }
  }

  if (existingTestId) {
    delete payload.assetId;
  }

  return { payload, pushButtonPayload, error: null };
}
